<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || resourceLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !resourceLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('name') }}</label>
          <p class="label-info">{{ $t('name-info') }}<br /><br /></p>
          <v-text-field
            v-model.trim="resource.name"
            outlined
            :placeholder="'Name'"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            data-cy="name"
            :disabled="!edit"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('resource-provider') }}</label>
          <p class="label-info">
            Full Name of the Provider/Organisation offering Services <br />
            and acting as main contact point for the Services
          </p>
          <v-autocomplete
            v-if="admin && countOrders == 0"
            v-model="resource.provider"
            :items="providers"
            :item-value="'id'"
            :item-text="'name'"
            return-object
            outlined
            clearable
            :disabled="!edit"
            data-cy="hostingInstitution"
          ></v-autocomplete>
          <v-text-field
            v-else
            v-model="providerName"
            outlined
            :disabled="true"
            :rules="requiredRule()"
            class="required"
            data-cy="hostingInstitution"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>Can be ordered</label>
          <p class="label-info">
            The "order" button is available only in services that have this enabled
          </p>
          <v-switch
            :disabled="!edit"
            v-model="resource.orderable"
            color="success"
            class="required"
          ></v-switch>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10">
                <v-row>
                  <v-col cols="12">
                    <label>Prerequisites</label>
                    <p class="label-info">
                      Requirements/prerequisities for the operation or use of the resource by users
                    </p>
                    <tiptap-vuetify
                      :placeholder="'Max length is 2000 characters'"
                      v-model="resource.prerequisites"
                      :extensions="extensions"
                      :disabled="!edit"
                      clearable
                    />
                    <div :class="charCount > maxChars ? 'red--text' : 'black--text'">
                      {{ charCount }} / {{ maxChars }} characters
                    </div>
                  </v-col>
                  <!-- <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('abbreviation') }}</label>
                    <p class="label-info">{{ $t('abbreviation-resource-message') }}</p>
                    <v-text-field
                      v-model="resource.abbreviation"
                      outlined
                      clearable
                      :disabled="!edit"
                      :placeholder="$t('abbreviation')"
                      :rules="maxLengthRule(30)"
                      data-cy="abbreviation"
                    ></v-text-field>
                  </v-col> -->
                  <v-col cols="4">
                    <label>{{ $t('webpage') }}</label>
                    <p class="label-info">
                      {{ $t('webpage-info') }}
                    </p>
                    <v-text-field
                      v-model="resource.webpage"
                      outlined
                      :placeholder="'Webpage'"
                      :disabled="!edit"
                      data-cy="website"
                      :rules="checkURL()"
                      prepend-inner-icon="mdi-link-variant"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="my-5" v-if="next" justify="end">
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        @click="add()"
        :disabled="!valid || charCount > maxChars"
        class="white--text font-weight-bold"
        data-cy="submit"
        >{{ $t('next') }}
        <v-icon color="white">$vuetify.icons.next</v-icon>
      </v-btn>
    </v-row>
    <v-row class="my-5" v-if="!next && edit" justify="end">
      <v-btn outlined class="black--text font-weight-bold mx-2" rounded x-large @click="cancel()">
        {{ $t('cancel') }}
      </v-btn>
      <v-btn
        depressed
        rounded
        x-large
        color="primary200"
        class="white--text font-weight-bold"
        @click="add()"
        :disabled="!valid || charCount > maxChars"
        >{{ $t('update') }}
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  Paragraph,
  ListItem,
  BulletList,
  OrderedList,
} from 'tiptap-vuetify';

export default {
  name: 'ResourceBasicInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    countOrders: {
      type: Number,
      default: 0,
    },
    id: {
      type: [Number, String],
      default: null,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
    resourceVisibility: {
      type: String,
      default: 'published',
    },
  },
  components: {
    AlertUnsavedChanges,
    TiptapVuetify,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -2,
      unsavedChanges: false,
      resource: {
        id: null,
        published: true,
        name: null,
        abbreviation: null,
        webpage: null,
        hostingInstitution: null,
        orderable: true,
        prerequisites: null,
      },
      providerName: null,
      providers: [],
      resourceLoading: true,
      extensions: [Bold, Italic, Underline, Paragraph, ListItem, BulletList, OrderedList],
      maxChars: 2000,
    };
  },
  mounted() {
    if (this.admin) {
      let id = this.$route.query.id;
      this.axios
        .get('/api/v1/resources/' + id + '/provider-light')
        .then(res => (this.providerName = res.data.name))
        .finally(() => {
          this.loadingComponents++;
        });
      this.axios
        .get('/api/v1/providers/all-light')
        .then(res => {
          this.providers = res.data;
        })
        .finally(() => {
          this.loadingComponents++;
        });
    } else {
      this.axios
        .get('/api/v1/provider-settings/' + sessionStorage.getItem('providerId') + '/light')
        .then(res => (this.providerName = res.data.name))
        .finally(() => {
          this.loadingComponents++;
          this.loadingComponents++;
        });
    }
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
      this.unsavedChanges = false;
    },
    add() {
      EventBus.$emit('waiting', true);
      let url = '/api/v1/resources/basic-info?providerId=';
      url += this.admin ? this.$route.query.id : sessionStorage.getItem('providerId');
      console.log(this.resource);
      console.log(url);
      this.axios
        .post(url, this.resource)
        .then(res => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('resource-updated'),
          });
          this.unsavedChanges = false;
          this.$emit('resourceId', res.data.id);
          this.$emit('tab', 1);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
    charCount() {
      const div = document.createElement('div');
      div.innerHTML = this.resource.prerequisites || '';
      return div.textContent.length;
    },
  },
  watch: {
    loading(value) {
      if (value === false) {
        console.log('Loading');
        if (this.id != null) {
          this.axios
            .get('/api/v1/resources/' + this.id)
            .then(res => {
              this.resource = res.data;
              this.$nextTick(() => {
                this.resourceLoading = false;
              });
            })
            .catch(error => {
              console.log({ error });
            });
        } else {
          this.resourceLoading = false;
        }
      }
    },
    resource: {
      handler: function(val, oldVal) {
        console.log('[Resource] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
    resourceVisibility(val) {
      if (this.id == null) {
        this.resource.published = val === 'published';
      }
    },
  },
};
</script>
